<template>
  <div class="page history">
    <h1 class="page-title">History</h1>
    <p class="page-lede">
      Every game you start is saved on this device, so you can pick an unfinished one back up.
    </p>

    <section v-if="current" class="now" aria-labelledby="now-title">
      <div class="now-text">
        <h2 id="now-title">Playing now</h2>
        <p class="now-meta">{{ current.title }}, {{ current.progress }}</p>
      </div>
      <router-link to="/game" class="btn btn-primary btn-small">Continue</router-link>
    </section>

    <div v-if="groups.length" class="days">
      <section v-for="group in groups" :key="group.label" class="day" :aria-label="group.label">
        <header class="day-head">
          <h2>{{ group.label }}</h2>
          <p class="day-count">
            {{ group.games.length }} {{ group.games.length === 1 ? 'game' : 'games' }}
          </p>
        </header>

        <ol class="games">
          <li
            v-for="game in group.games"
            :key="game.id"
            class="game"
            :class="{ 'is-open': expandedId === game.id }"
          >
            <button
              type="button"
              class="game-row"
              :aria-expanded="expandedId === game.id"
              :aria-controls="`game-${game.id}`"
              @click="toggle(game.id)"
            >
              <span class="row-main">
                <span class="row-title">{{ game.title }}</span>
                <span class="row-players">
                  <span v-for="player in game.others" :key="player.id" class="row-player">
                    {{ player.name }} <span class="row-player-total">{{ player.total }}</span>
                  </span>
                </span>
              </span>
              <span class="row-side">
                <span class="row-score">{{ game.score }}</span>
                <span class="row-when">{{ game.when }}</span>
              </span>
              <svg class="chevron" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path
                  d="M6 9l6 6 6-6"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>

            <div v-if="expandedId === game.id" :id="`game-${game.id}`" class="game-detail">
              <p class="detail-meta">{{ game.meta }}</p>
              <Standings :players="game.players" :rounds="game.rounds" :show-moves="false" />
              <div class="detail-actions">
                <button
                  type="button"
                  class="text-btn"
                  :aria-expanded="sheetOpen"
                  :aria-controls="`sheet-${game.id}`"
                  @click="sheetOpen = !sheetOpen"
                >
                  {{ sheetOpen ? 'Hide scoresheet' : 'Show scoresheet' }}
                </button>
                <span class="spacer"></span>
                <button
                  type="button"
                  class="text-btn delete"
                  :aria-label="`Delete ${game.title}, ${game.meta}`"
                  @click="pendingDelete = game"
                >
                  Delete
                </button>
                <button
                  v-if="!game.finishedAt"
                  type="button"
                  class="btn btn-secondary btn-small"
                  @click="resume(game)"
                >
                  Resume
                </button>
              </div>
              <div v-if="sheetOpen" :id="`sheet-${game.id}`" class="sheet">
                <ScoreSheet :players="game.players" :rounds="game.rounds" />
              </div>
            </div>
          </li>
        </ol>
      </section>
    </div>

    <div v-else-if="!current" class="empty">
      <p>No games yet. Games show up here as soon as the first round is played.</p>
      <router-link to="/new-game" class="btn btn-primary">New game</router-link>
    </div>

    <AppModal
      :is-open="!!pendingDelete"
      title="Delete this game?"
      :subtitle="pendingDelete?.title"
      size="sm"
      @close="pendingDelete = null"
    >
      <p class="delete-note">This can't be undone.</p>
      <template #footer>
        <button type="button" class="btn btn-ink btn-block" @click="confirmDelete">
          Delete game
        </button>
      </template>
    </AppModal>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useGameStore } from '../stores/game';
import { formatTotal, playerTotal } from '../utils/scores';
import AppModal from '../components/AppModal.vue';
import ScoreSheet from '../components/ScoreSheet.vue';
import Standings from '../components/Standings.vue';

const router = useRouter();
const gameStore = useGameStore();

const expandedId = ref(null);
const sheetOpen = ref(false);
const pendingDelete = ref(null);

const now = ref(new Date());
const refreshNow = () => {
  if (document.visibilityState === 'visible') now.value = new Date();
};
onMounted(() => document.addEventListener('visibilitychange', refreshNow));
onUnmounted(() => document.removeEventListener('visibilitychange', refreshNow));

const listFormat = new Intl.ListFormat('en-GB', { type: 'conjunction' });

const timeFormat = new Intl.DateTimeFormat(undefined, { timeStyle: 'short' });
const dayFormat = new Intl.DateTimeFormat(undefined, {
  weekday: 'long',
  day: 'numeric',
  month: 'long'
});
const monthFormat = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' });
const shortDayFormat = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  day: 'numeric',
  month: 'short'
});
const fullFormat = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' });

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const isRecent = (date) => {
  const weekAgo = startOfDay(now.value);
  weekAgo.setDate(weekAgo.getDate() - 6);
  return date >= weekAgo;
};

const sameDay = (a, b) => a.toDateString() === b.toDateString();

const groupLabel = (date) => {
  if (!isRecent(date)) return monthFormat.format(date);
  const yesterday = new Date(now.value);
  yesterday.setDate(yesterday.getDate() - 1);
  if (sameDay(date, now.value)) return 'Today';
  if (sameDay(date, yesterday)) return 'Yesterday';
  return dayFormat.format(date);
};

const describe = (game) => {
  const standings = game.players
    .map((player) => ({
      id: player.id,
      name: player.name,
      total: playerTotal(game.rounds, player.id)
    }))
    .sort((a, b) => b.total - a.total);
  const best = standings[0].total;
  const leaders = standings.filter((player) => player.total === best);
  const isTie = leaders.length > 1;
  const leaderNames = listFormat.format(leaders.map((player) => player.name));
  const maxRound = game.players.length * game.miniGames.length;
  const playedAt = new Date(game.finishedAt ?? game.updatedAt ?? game.startedAt);
  const progress = `round ${game.rounds.length + 1} of ${maxRound}`;
  return {
    ...game,
    score: formatTotal(best),
    playedAt,
    progress,
    title: `${leaderNames} ${game.finishedAt ? (isTie ? 'tied' : 'won') : isTie ? 'lead' : 'leads'}`,
    others: standings
      .slice(leaders.length)
      .map((player) => ({ id: player.id, name: player.name, total: formatTotal(player.total) })),
    when: game.finishedAt
      ? isRecent(playedAt)
        ? timeFormat.format(playedAt)
        : shortDayFormat.format(playedAt)
      : 'Unfinished',
    meta: game.finishedAt
      ? `Finished ${fullFormat.format(playedAt)} after ${game.rounds.length} rounds`
      : `Stopped at ${progress}, last played ${fullFormat.format(playedAt)}`
  };
};

const current = computed(() => {
  const game = gameStore.history.find(
    (entry) => entry.id === gameStore.gameId && !entry.finishedAt
  );
  return game ? describe(game) : null;
});

const groups = computed(() => {
  const byLabel = new Map();
  gameStore.history
    .filter((entry) => entry.id !== current.value?.id)
    .map(describe)
    .sort((a, b) => b.playedAt - a.playedAt)
    .forEach((game) => {
      const label = groupLabel(game.playedAt);
      if (!byLabel.has(label)) byLabel.set(label, []);
      byLabel.get(label).push(game);
    });
  return [...byLabel].map(([label, games]) => ({ label, games }));
});

const toggle = (id) => {
  expandedId.value = expandedId.value === id ? null : id;
  sheetOpen.value = false;
};

const resume = (game) => {
  if (gameStore.resumeFromHistory(game.id)) router.push('/game');
};

const confirmDelete = () => {
  if (!pendingDelete.value) return;
  gameStore.deleteHistoryEntry(pendingDelete.value.id);
  expandedId.value = null;
  pendingDelete.value = null;
};
</script>

<style scoped>
.history {
  max-width: 720px;
}

.page-lede {
  margin-bottom: 1.5rem;
}

.btn-small {
  flex: none;
  min-height: 40px;
  padding: 0.375rem 1rem;
}

.now {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 2rem;
  padding: 1rem;
  border-radius: 14px;
  border: 1px solid var(--panel-edge);
  background-color: var(--panel-solid);
}

.now-text {
  min-width: 0;
}

.now h2 {
  font-size: var(--step-1);
}

.now-meta {
  color: var(--on-table-muted);
  font-size: var(--step--1);
}

.days {
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
}

.day-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.5rem;
  padding: 0 0.25rem;
}

.day-head h2 {
  font-size: var(--step-1);
}

.day-count {
  color: var(--on-table-muted);
  font-size: var(--step--1);
  white-space: nowrap;
}

.games {
  list-style: none;
  padding: 0.375rem;
  border-radius: 14px;
  border: 1px solid var(--panel-edge);
  background-color: var(--panel-solid);
}

.game {
  border-radius: var(--radius-card);
}

.game.is-open {
  background-color: var(--table-raised);
}

.game + .game {
  margin-top: 0.125rem;
}

.game-row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-height: 3.75rem;
  padding: 0.5rem 0.5rem 0.5rem 0.75rem;
  border-radius: var(--radius-card);
  text-align: left;
  transition: background-color 0.15s;
}

.game-row:hover {
  background-color: var(--table-raised);
}

.row-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}

.row-title {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.row-players {
  display: flex;
  flex-wrap: wrap;
  column-gap: 0.875rem;
  color: var(--on-table-muted);
  font-size: var(--step--1);
}

.row-player {
  white-space: nowrap;
}

.row-player-total {
  color: var(--on-table);
  font-weight: 500;
}

.row-side {
  flex: none;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.2;
}

.row-score {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 1.375rem;
}

.row-when {
  color: var(--on-table-muted);
  font-size: 0.8125rem;
}

.chevron {
  flex: none;
  color: var(--on-table-muted);
  transition: transform 0.2s;
}

.is-open .chevron {
  transform: rotate(180deg);
}

.game-detail {
  padding: 0 0.75rem 0.5rem;
}

.detail-meta {
  margin-bottom: 0.625rem;
  color: var(--on-table-muted);
  font-size: var(--step--1);
}

.detail-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.spacer {
  flex: 1;
}

.text-btn {
  min-height: 44px;
  padding: 0 0.5rem;
  border-radius: var(--radius-control);
  color: var(--on-table);
  font-weight: 600;
  font-size: var(--step--1);
}

.text-btn:hover {
  background-color: var(--panel-solid);
}

.text-btn.delete {
  color: var(--on-table-muted);
}

.text-btn.delete:hover {
  color: var(--red-on-table);
}

.sheet {
  margin: 0.25rem 0 0.5rem;
  padding: 0.25rem 0.25rem 0;
  border-radius: var(--radius-card);
  border: 1px solid var(--panel-edge);
  background-color: var(--panel-solid);
  overflow: hidden;
}

.delete-note {
  color: var(--on-table-muted);
}

.empty {
  padding: 2rem 1rem;
  border: 1px dashed var(--table-line);
  border-radius: 14px;
  text-align: center;
  color: var(--on-table-muted);
}

.empty .btn {
  margin-top: 1rem;
}
</style>
